// Phase-3 Slice 27 + 36 — step 1 of the rename plan: the SCHEMA-side ALTER.
//
// Extracted from `renamePlanner.ts`, which stays the facade every caller
// imports. This module owns only the statement that renames the catalog object
// itself (forward + undo):
//   - column     → dialect.ts `synthesizeAlterRenameColumn`
//   - table/view → per-engine emitter `emitTableOrViewSchemaAlter`
// Rewriting the bodies that REFERENCE the renamed object is the separate
// `renameReferenceCollection.ts` → `renameObjectBodyEdits.ts` pipeline.

import type { SqlEngine } from '../../sql-grammar/model';
import { synthesizeAlterRenameColumn } from '../../sql-resolver/ddl/apply/dialect';
import type { FetchObjectBodyOptions } from '../index/objectBodyFetcher';
import type { AlterStatement, RenameRequest } from '../types';

import type { DialectRenameEmitter } from './emit';
import type { RenameDestination } from './renameDestination';

export type PlanSchemaAlterInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly request: RenameRequest;
  /** The destination identity the whole plan shares — the decoded new name,
   *  the delimiters the user typed around it, and the spelling this statement
   *  will leave in the catalog (`renameDestination.ts`). The ALTER emits the
   *  DECODED name so a typed delimited destination is wrapped once, not twice. */
  readonly destination: RenameDestination;
  /** The destination the UNDO statement below restores — the ORIGINAL object's
   *  catalog identity, derived by the same helper from the same table
   *  (`renameDestination.resolveRestoreDestination`). The undo is the one
   *  statement in the plan whose target is not a name the user typed but bytes
   *  the server already held, and it is the direction that drifted: its
   *  delimiters live on `undoDestination.quoting`, and what it leaves in the
   *  catalog on `undoDestination.catalogName`, so the tab pass reads the answer
   *  instead of predicting it. */
  readonly undoDestination: RenameDestination;
  readonly sqlEngine: SqlEngine;
  /** The per-dialect emitter, or `null` when the engine has none. A column
   *  rename never needs it; a table/view rename cannot proceed without it. */
  readonly emitter: DialectRenameEmitter | null;
  readonly fetchBody: (options: FetchObjectBodyOptions) => Promise<string | null>;
};

export type SchemaAlterPlan = {
  readonly schemaAlter: AlterStatement;
  readonly undoSchemaAlter: AlterStatement;
};

/** Synthesize the forward and undo schema-side rename statements.
 *
 *  Returns `null` for exactly one reason: a table/view rename on an engine with
 *  no per-dialect emitter — which the planner surfaces as its `missing-emitter`
 *  status. The column branch has no such failure mode (the dialect synthesizer
 *  covers every engine), so it always returns a plan. */
export async function planSchemaAlter(
  input: PlanSchemaAlterInput,
): Promise<SchemaAlterPlan | null> {
  const {
    connectionId,
    database,
    request,
    destination,
    undoDestination,
    sqlEngine,
    emitter,
    fetchBody,
  } = input;

  if (request.kind === 'column') {
    const containingTable = request.containingTable!;
    return {
      schemaAlter: {
        sql: synthesizeAlterRenameColumn(
          sqlEngine,
          containingTable,
          request.fromIdentifier.name,
          destination.name,
          containingTable.quoted,
          destination.quoting,
          // The OLD column is a CATALOG SPELLING here, not a reference the
          // engine still folds: `go-to/columnAtCursor.ts` binds the column
          // name off the Object Explorer tree, so it is already the bytes the
          // server holds. Without this flag the fold that RESOLVES a script's
          // reference CORRUPTS this one — `SELECT users."Id" FROM users` has a
          // bare host, so `quoteNames` is false and the statement went out as
          // `... RENAME COLUMN Id TO ...`, asking PostgreSQL for `id` and
          // Oracle for `ID`. It is the FIRST statement of the batch, so the
          // whole rename stopped there. See `dialect.ts`'s three origins.
          'catalog-spelling',
        ),
        purpose: 'schema-rename',
      },
      // The undo names the column this statement CREATED (`destination`'s
      // catalog spelling), not the one the user typed — an unquoted PostgreSQL
      // `... TO User_Id` makes `user_id`, and only that name can be renamed back.
      undoSchemaAlter: {
        sql: synthesizeAlterRenameColumn(
          sqlEngine,
          containingTable,
          destination.catalogName,
          undoDestination.name,
          containingTable.quoted,
          // The undo's DESTINATION is a catalog spelling too — the only
          // statement in the plan whose target is not a name the user typed
          // but one the server already held. So the forward ALTER's rule (fold
          // freely, the fold IS the intent) inverts here: an undo that emits a
          // bare `Id` restores `id` on PostgreSQL and `ID` on Oracle, a column
          // the buffers the undo pass rewrites back to `Id` do not name. The
          // engine's own delimiters are the way to ask for the exact bytes, and
          // `resolveRestoreDestination` settled which they are — for this
          // statement AND for the tab pass that follows the undo, which is what
          // stops the two from disagreeing. (`maybeQuoteIdent` reads only
          // whether this is set, not which style, and still prefers a quoted
          // host's own.)
          undoDestination.quoting,
          // Same contract, one step later: `catalogName` is what the forward
          // ALTER left in the catalog (`renameDestination.ts` derives it), so a
          // case-exact destination — a delimited `"MixedCase"`, or any name on
          // an engine that stores the typed case — could not be named back
          // without delimiters.
          'catalog-spelling',
        ),
        purpose: 'schema-rename',
      },
    };
  }

  // table / view rename
  if (!emitter) return null;
  // NAV-2: a view rename must NEVER lose the view definition. Engines
  // without a native view-rename (MySQL) recreate the view from its real
  // body — so the planner fetches the renamed view's OWN body here and
  // threads it into the emitter. The view body is unchanged by the rename
  // itself, so the same body feeds both the forward emit (recreate `to`)
  // and the undo emit (recreate `from`). Oracle/SQLite do a real, in-place
  // RENAME and ignore `viewBody`; MySQL refuses (typed unrewritable) rather
  // than emit the data-destroying placeholder when it is absent.
  // Table renames never need a body — skip the fetch entirely (→ undefined).
  // `fetchRows` throws on a failed catalog query; here a failure degrades
  // exactly like a missing body (the emitters that need one refuse).
  const viewBody =
    request.kind === 'view'
      ? (await fetchBody({
          connectionId,
          database,
          engineId: request.engineId,
          objectKind: 'view',
          objectName: request.fromIdentifier,
        }).catch(() => null)) ?? undefined
      : undefined;
  return {
    schemaAlter: {
      sql: emitter.emitTableOrViewSchemaAlter({
        kind: request.kind,
        from: request.fromIdentifier,
        // The DECODED destination: every emitter delimits (or literalises) the
        // target itself, so handing it a spelling that still carries the user's
        // own delimiters produced a relation whose NAME contains quote
        // characters.
        to: destination.identifier,
        viewBody,
      }),
      purpose: 'schema-rename',
    },
    undoSchemaAlter: {
      sql: emitter.emitTableOrViewSchemaAlter({
        kind: request.kind,
        from: destination.identifier,
        // The restore destination, for the same reason the forward leg takes
        // the forward one: it is the identity the undo's own tab pass then
        // matches buffers against, and the two must be the one object.
        to: undoDestination.identifier,
        viewBody,
      }),
      purpose: 'schema-rename',
    },
  };
}
